import jwtDecode from 'jwt-decode'
import { Capacitor } from '@capacitor/core'
import { FCM } from '@capacitor-community/fcm';
import { PushNotifications } from '@capacitor/push-notifications';
import { OAuth2Client } from '@byteowls/capacitor-oauth2'

const topics = {
  'news': 'Новини',
  'events': 'Събития',
  'campaigns': 'Кампании',
  'questionnaires': 'Анкети'
}

async function updateUserNotifications(userData) {
  if (Capacitor.isPluginAvailable('PushNotifications') && typeof userData.notifications !== 'undefined') {
    await PushNotifications.register()

    for (let topic in topics) {
      if (typeof userData.notifications[topic] !== 'undefined') {
        if (userData.notifications[topic]) {
          FCM.subscribeTo({ topic })
        } else {
          FCM.unsubscribeFrom({ topic })
        }
      }

      if (userData.roles.includes('administrator')) {
        const testTopic = topic + '-test'
        FCM.subscribeTo({ topic: testTopic })
      }
    }
  }
}

export default function(axios, api) {
  return {
    state: {
      authToken: null,
      user: {
        roles: [],
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        notifications: {}
      },
      notificationTopics: topics,
      localOrganizations: []
    },

    getters: {
      authToken: state => state.authToken,
      localOrganizations: state => state.localOrganizations,
      user: state => state.user,
      isMember(state) {
        return state.user && state.user.roles && state.user.roles.indexOf('subscriber') === -1
      },
      loggedIn(state) {
        if (state.authToken !== null) {
          const decodedToken = jwtDecode(state.authToken)
          if (decodedToken.exp && (decodedToken.exp * 1000) > Date.now()) {
            return true
          }
        }

        return false
      },
      topics: state => state.notificationTopics
    },

    actions: {
      login({ commit, dispatch }, { username, password }) {
        return new Promise((resolve, reject) => {
          api.getTokenFor(username, password).then(response => {
            if (response.data.token) {
              const decodedToken = jwtDecode(response.data.token)
              if (decodedToken.exp && (decodedToken.exp * 1000) > Date.now()) {
                // successful login
                commit('setAuthToken', response.data.token)

                let userId = decodedToken.data.user.id

                dispatch('getUser', userId).then(() => {
                  // commit('setUserData', response.data)
                  resolve()
                })
              } else {
                // login failed
                commit('setAuthToken', null)
                reject(new Error('Token invalid or expired'))
              }
            }
          }).catch(error => reject(error))
        })
      },

      loginFacebook() {
        console.log('logged in fb')
      },

      loginGoogle({ dispatch }) {
        return new Promise((resolve, reject) => {
          OAuth2Client.authenticate({
            appId: process.env.VUE_APP_GOOGLE_WEB_APP_ID,
            authorizationBaseUrl: 'https://accounts.google.com/o/oauth2/auth',
            accessTokenEndpoint: 'https://www.googleapis.com/oauth2/v4/token',
            scope: 'email profile',
            resourceUrl: 'https://www.googleapis.com/userinfo/v2/me',
            web: {
              responseType: "token",
              accessTokenEndpoint: "",
              redirectUrl: process.env.VUE_APP_GOOGLE_WEB_REDIRECT_URL,
              windowOptions: 'height=600,left=0,top=0'
            },
            android: {
              appId: process.env.VUE_APP_GOOGLE_ANDROID_APP_ID,
              responseType: 'code',
              redirectUrl: process.env.VUE_APP_BUNDLE_ID + ':/'
            },
            ios: {
              appId: process.env.VUE_APP_GOOGLE_IOS_APP_ID,
              responseType: 'code',
              redirectUrl: process.env.VUE_APP_BUNDLE_ID + ':/'
            }
          }).then(response => {
            dispatch('login', {
              username: response.name,
              password: `google_token:${response.access_token}`
            }).then(() => resolve()).catch(() => reject())
          }).catch(reason => {
            console.log(reason);
            reject(reason)
          });
        })
      },

      initUser({ commit, dispatch, state }) {
        const savedToken = localStorage.getItem('authToken')
        if (savedToken && state.authToken !== savedToken) {
          try {
            const decodedToken = jwtDecode(savedToken)
            if (decodedToken.exp && (decodedToken.exp * 1000) > Date.now()) {
              // set token
              commit('setAuthToken', savedToken)

              // get user
              dispatch('getUser', decodedToken.data.user.id)

              // verify token
              api.verifyToken()
                .catch(() => {
                  // invalid or expired token
                  commit('setAuthToken', null)
                  commit('setUserData', null)
                  localStorage.removeItem('authToken')
                })
            }
          } catch (e) {
            commit('setAuthToken', null)
            localStorage.removeItem('authToken')
            localStorage.removeItem('user')
          }
        }
      },

      getUser({ commit, dispatch }, id) {
        return api.getUser(id).then(user => {
          let userData = user.data
          userData.roles = user.roles

          commit('setUserData', userData)

          // update user notification subscriptions
          updateUserNotifications(userData)

          if (userData.roles.indexOf('administrator') > -1 ||
            userData.roles.indexOf('nc_member') > -1) {
            dispatch('getNationalCouncilCategoryId')
          }
        })
      },

      registerUser({ commit }, user) {
        return new Promise((resolve, reject) => {
          api.registerUser(user).then(response => {
            resolve(response)
          }).catch((error) => {
            // request error
            reject(error)
          })
        })
      },

      updateUser({ commit }, user) {
        updateUserNotifications(user)

        return new Promise((resolve, reject) => {
          api.updateUser(user).then(response => {
            resolve(response)
          }).catch((error) => {
            // request error
            reject(error)
          })
        })
      },

      removeUser() {
        return new Promise((resolve, reject) => {
          api.removeUser().then(response => {
            resolve(response)
          }).catch((error) => {
            // request error
            reject(error)
          })
        })
      },

      resetPassword({ commit }, email) {
        return api.resetPassword(email)
      },

      updatePassword({ commit }, { userID, token, password }) {
        return api.updatePassword(userID, token, password)
      },

      logout({ commit }) {
        return new Promise((resolve) => {
          commit('setAuthToken', null)
          resolve()
        })
      },

      getLocalOrganizations({ commit }) {
        return api.fetchLocalOrganizations().then(response => {
          commit('setLocalOrganizations', response.data)
        })
      }
    },

    mutations: {
      setLocalOrganizations(state, organizations) {
        state.localOrganizations = organizations
      },

      setAuthToken(state, authToken) {
        state.authToken = authToken
        state.loggedIn = !!authToken
        if (authToken) {
          localStorage.setItem('authToken', authToken)
        } else {
          localStorage.removeItem('authToken')
        }
      },

      setUserData(state, data) {
        const user = {
          ID: parseInt(data.ID),
          email: data.user_email,
          name: data.display_name,
          first_name: data.first_name,
          last_name: data.last_name,
          roles: data.roles,
          notifications: data.notifications || {},
          vote_email: data.vote_email,
          localOrganization: data.localOrganization
        }

        state.user = user
        localStorage.setItem('user', JSON.stringify(user))
      }

    }
  }
}
